import type { ApiResponse, Pagination } from '@/shared/types/api';
import { api } from '../../../shared/api/client';
import type {
  PaymentStatus,
  ResidentFiltersType,
  ResidentPaymentFrequency,
  ResidentStatus,
} from '../components';

export interface Resident {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  moveInDate: string;
  moveOutDate?: string;
  isActive: boolean;
  notes?: string;
  avatar?: string;
  sobrietyDate?: string;
  sponsor?: string;
  sponsorPhone?: string;
  homeGroup?: string;
  roomNumber?: string;
  monthlyRate?: number;
  paymentStatus?: 'current' | 'late' | 'pending';
  status?: 'active' | 'archived' | 'pending';
  createdAt: string;
  updatedAt: string;
  houseId: string;
  house: {
    id: string;
    name: string;
    address: string;
  };
  payments?: Array<{
    id: string;
    amount: number;
    dueDate: string;
    paidDate?: string;
    status: 'paid' | 'pending' | 'overdue';
  }>;
}

export interface CreateResidentData {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  moveInDate: string;
  notes?: string;
  sobrietyDate?: string;
  sponsor?: string;
  sponsorPhone?: string;
  homeGroup?: string;
  roomNumber?: string;
  monthlyRate?: number;
  houseId: string;
}

export interface ResidentStats {
  financial: {
    totalPaid: number;
    totalOwed: number;
    overdueCount: number;
    overdueAmount: number;
  };
  residency: {
    daysInHouse: number;
    moveInDate: string;
    moveOutDate?: string;
    isActive: boolean;
  };
  sobriety: {
    sobrietyDate?: string;
    daysSober?: number;
    sponsor?: string;
    homeGroup?: string;
  };
}

export const residentApi = {
  // Get all residents
  getResidents: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    houseId?: string;
    isActive?: boolean;
  }): Promise<{
    residents: Resident[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.houseId) searchParams.append('houseId', params.houseId);
    if (params?.isActive !== undefined)
      searchParams.append('isActive', params.isActive.toString());

    return api.get<{
      residents: Resident[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/residents?${searchParams.toString()}`);
  },

  // // Get resident by ID
  // getResidentById: async (id: string): Promise<Resident> => {
  //   return api.get<Resident>(`/residents/${id}`);
  // },

  // Create resident
  // createResident: async (data: CreateResidentData): Promise<Resident> => {
  //   return api.post<Resident>('/residents', data);
  // },

  // Update resident
  // updateResident: async (
  //   id: string,
  //   data: Partial<CreateResidentData>,
  // ): Promise<Resident> => {
  //   return api.put<Resident>(`/residents/${id}`, data);
  // },

  // Move out resident
  moveOutResident: async (
    id: string,
    data: {
      moveOutDate: string;
      notes?: string;
    },
  ): Promise<Resident> => {
    return api.post<Resident>(`/residents/${id}/move-out`, data);
  },

  // Get resident statistics
  getResidentStats: async (id: string): Promise<ResidentStats> => {
    return api.get<ResidentStats>(`/residents/${id}/stats`);
  },

  // Get archived residents with additional archive information
  getArchivedResidents: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    houseId?: string;
  }): Promise<{
    residents: (Resident & {
      archiveDate?: string;
      archiveReason?: string;
      stayDuration?: number;
    })[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.houseId) searchParams.append('houseId', params.houseId);
    searchParams.append('isActive', 'false'); // Only archived residents

    return api.get<{
      residents: Resident[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/residents/archived?${searchParams.toString()}`);
  },

  // Restore archived resident
  restoreResident: async (id: string): Promise<Resident> => {
    return api.post<Resident>(`/residents/${id}/restore`);
  },

  /**
   * Resident Management apis
   */

  // ✅ POST /residents
  createResident: async (
    payload: CreateResidentPayload,
  ): Promise<CreateResidentResponse> => {
    try {
      const response = await api.post<ApiResponse<CreateResidentResponse>>(
        '/residents',
        payload,
      );

      // return actual data object
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to create resident',
      );
    }
  },

  async getAllResidentWithFilters(filters: ResidentFiltersType) {
    const { page, limit, search, status, paymentStatus, moveInDate, location } =
      filters;

    const response = await api.get<ApiResponse<ResidentsApiResponse>>(
      '/residents',
      {
        params: {
          page,
          limit,
          search: search || undefined,
          status: status || undefined,
          paymentStatus: paymentStatus || undefined,
          moveInDate: moveInDate || undefined,
          location: location || undefined,
        },
      },
    );

    return response.data;
  },

  // ✅ Get Resident Details by ID
  getResidentById: async (residentId: string): Promise<ResidentDetails> => {
    const response = await api.get<ApiResponse<ResidentDetails>>(
      `/residents/${residentId}`,
    );
    return response.data;
  },

  // ✅ Delete resident by ID
  deleteResident: async (residentId: string): Promise<null> => {
    const response = await api.delete<ApiResponse<null>>(
      `/residents/${residentId}`,
    );
    return response.data;
  },

  // ✅ Update Resident Status
  updateResidentStatus: async (
    residentId: string,
    payload: UpdateResidentStatusPayload,
  ): Promise<null> => {
    const response = await api.patch<ApiResponse<null>>(
      `/residents/${residentId}/status`,
      payload,
    );
    return response.data;
  },

  async getArchivedResidentWithFilters(filters: ResidentFiltersType) {
    const { page, limit, search, status, paymentStatus, moveInDate, location } =
      filters;

    const response = await api.get<ApiResponse<ArchivedResidentsResponse>>(
      '/residents/archived',
      {
        params: {
          page,
          limit,
          search: search || undefined,
          status: status || undefined,
          paymentStatus: paymentStatus || undefined,
          moveInDate: moveInDate || undefined,
          location: location || undefined,
        },
      },
    );

    return response.data;
  },

  updateResident: async ({
    residentId,
    payload,
  }: {
    residentId: string;
    payload: UpdateResidentPayload;
  }): Promise<ResidentDetails> => {
    try {
      const response = await api.put<ApiResponse<ResidentDetails>>(
        `/residents/${residentId}`,
        payload,
      );

      // ✅ return the actual data object (inside response.data.data)
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to update resident',
      );
    }
  },

  // ✅ Get upload URL for residents (uses resident's organization from profile)
  getResidentUploadUrl: async (
    filename: string,
    module: string,
    contentType: string,
  ): Promise<{ signedUrl: string; path: string; token?: string }> => {
    const response = await api.post<
      ApiResponse<{ signedUrl: string; path: string; token?: string }>
    >('/resources/resident-upload-url', {
      filename,
      module,
      contentType,
    });
    return response.data;
  },
};

// ✅ Request payload type
export interface CreateResidentPayload {
  fullName: string;
  email?: string;
  phone: string;
  houseId: string;
  bedNo: string;
  moveInDate: string;
  monthlyFees: number;
  feeFrequency: 'MONTHLY' | 'WEEKLY' | 'YEARLY';
  securityDeposit: number;
  managerEmail?: string;
}

// ✅ Response type (matches API response)
export interface CreateResidentResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    phone: string | null;
    avatar: string | null;
    isActive: boolean;
    isEmailVerified: boolean;
    createdAt: string;
    updatedAt: string;
  };
  residentProfile: {
    id: string;
    userId: string;
    houseId: string;
    bedNo: string;
    moveInDate: string;
    moveOutDate: string | null;
    monthlyFees: number;
    feeFrequency: string;
    securityDeposit: number;
    status: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface ResidentUser {
  id: string;
  name: string;
  email: string;
  phone: string;
}

export interface ResidentHouse {
  id: string;
  name: string;
  address: string;
  city: string;
}

export interface ResidentApiType {
  id: string;
  status: ResidentStatus;
  moveInDate: string;
  bedNo: string;
  monthlyFees: number;
  feeFrequency: 'MONTHLY' | 'WEEKLY' | 'YEARLY';
  securityDeposit: number;
  paymentStatus: PaymentStatus;
  user: ResidentUser;
  house: ResidentHouse;
  manager: any | null;
}

export interface ResidentsApiResponse {
  residents: ResidentApiType[];
  pagination: Pagination;
}

export interface Payment {
  id: string;
  amount: number;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  method: string;
  createdAt: string;
  updatedAt: string;
  invoiceId?: string;
  meta?: any;
}

export interface ResidentDetails {
  id: string;
  fullname: string;
  email: string;
  phone: string;
  avatar?: string | null;
  moveInDate: string;
  house: {
    id: string;
    name: string;
  };
  manager: string | null;
  monthlyFees: number;
  feeFrequency: ResidentPaymentFrequency;
  securityDeposit: number;
  status: ResidentStatus;
  selfRegistered: boolean;
  createdAt: string;
  payments?: {
    id: string;
    date: string;
    description: string;
    amount: string;
    paymentStatus: 'PAID' | 'PENDING' | 'OVERDUE';
  }[];
}

export interface UpdateResidentStatusPayload {
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
}

export interface ArchivedResidentsResponse {
  residents: ArchivedResident[];
  pagination: Pagination;
}

export interface ArchivedBy {
  id: string;
  name: string;
}

export interface ArchivedResident {
  id: string;
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  moveInDate: string;
  moveOutDate: string | null;
  bedNo: string;
  monthlyFees: number;
  feeFrequency: 'MONTHLY' | 'YEARLY';
  securityDeposit: number;
  paymentStatus: 'PAID' | 'UNPAID' | 'PARTIAL';
  archivedBy: ArchivedBy;
  archivedAt: string;
  user: ResidentUser;
  house: ResidentHouse;
}

export type UpdateResidentPayload = {
  fullName?: string;
  email?: string;
  phone?: string;
  houseId?: string;
  bedNo?: string;
  moveInDate?: string;
  monthlyFees?: number;
  feeFrequency?: 'MONTHLY' | 'WEEKLY' | 'YEARLY';
  securityDeposit?: number;
  managerEmail?: string;
};
